import { lazy, Suspense, useEffect, useMemo, useState } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { VendorAuthProvider, AuthGuard, GuestGuard, BusinessGuard } from '@cravo/auth'
import { platformConfigApi } from '@cravo/api-service'
import { PushNotificationsBootstrap } from '@/components/PushNotificationsBootstrap'
import { MaintenanceGate, compareVersions } from '@/components/MaintenanceGate'

// Current build version — bump on each deploy; compared against backend
// `minWebVersion` for force-update gating.
const APP_VERSION = '1.0.0'

import { AuthLayout } from '@/layouts/AuthLayout'
import { DashboardLayout } from '@/layouts/DashboardLayout'
import { OnboardingLayout } from '@/layouts/OnboardingLayout'

// Auth pages
const WelcomePage = lazy(() => import('@/pages/auth/WelcomePage').then(m => ({ default: m.WelcomePage })))
const LoginPage = lazy(() => import('@/pages/auth/LoginPage').then(m => ({ default: m.LoginPage })))
const SignUpPage = lazy(() => import('@/pages/auth/SignUpPage').then(m => ({ default: m.SignUpPage })))
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage').then(m => ({ default: m.ForgotPasswordPage })))
const PhoneLoginPage = lazy(() => import('@/pages/auth/PhoneLoginPage').then(m => ({ default: m.PhoneLoginPage })))
const CompleteProfilePage = lazy(() => import('@/pages/auth/CompleteProfilePage').then(m => ({ default: m.CompleteProfilePage })))

// Onboarding pages
const EntityTypePage = lazy(() => import('@/pages/onboarding/EntityTypePage').then(m => ({ default: m.EntityTypePage })))
const BusinessRegistrationPage = lazy(() => import('@/pages/onboarding/BusinessRegistrationPage').then(m => ({ default: m.BusinessRegistrationPage })))
const ApprovalPendingPage = lazy(() => import('@/pages/onboarding/ApprovalPendingPage').then(m => ({ default: m.ApprovalPendingPage })))

// Dashboard pages
const DashboardPage = lazy(() => import('@/pages/dashboard/DashboardPage').then(m => ({ default: m.DashboardPage })))
const RequestsPage = lazy(() => import('@/pages/requests/RequestsPage').then(m => ({ default: m.RequestsPage })))
const OrdersPage = lazy(() => import('@/pages/orders/OrdersPage').then(m => ({ default: m.OrdersPage })))
const OrderDetailPage = lazy(() => import('@/pages/orders/OrderDetailPage').then(m => ({ default: m.OrderDetailPage })))
const OrderChatPage = lazy(() => import('@/pages/orders/OrderChatPage').then(m => ({ default: m.OrderChatPage })))
const MenuPage = lazy(() => import('@/pages/menu/MenuPage').then(m => ({ default: m.MenuPage })))
const AiPage = lazy(() => import('@/pages/ai/AiPage').then(m => ({ default: m.AiPage })))
const AnalyticsPage = lazy(() => import('@/pages/analytics/AnalyticsPage').then(m => ({ default: m.AnalyticsPage })))
const ItemAnalyticsPage = lazy(() => import('@/pages/analytics/ItemAnalyticsPage').then(m => ({ default: m.ItemAnalyticsPage })))
const WalletPage = lazy(() => import('@/pages/wallet/WalletPage').then(m => ({ default: m.WalletPage })))
const SettlementsPage = lazy(() => import('@/pages/settlements/SettlementsPage').then(m => ({ default: m.SettlementsPage })))
const BankAccountsPage = lazy(() => import('@/pages/bank/BankAccountsPage').then(m => ({ default: m.BankAccountsPage })))
const KycPage = lazy(() => import('@/pages/kyc/KycPage').then(m => ({ default: m.KycPage })))
const WithdrawalRequestPage = lazy(() => import('@/pages/withdrawals/WithdrawalRequestPage').then(m => ({ default: m.WithdrawalRequestPage })))
const WithdrawalHistoryPage = lazy(() => import('@/pages/withdrawals/WithdrawalHistoryPage').then(m => ({ default: m.WithdrawalHistoryPage })))
const WithdrawalDetailPage = lazy(() => import('@/pages/withdrawals/WithdrawalDetailPage').then(m => ({ default: m.WithdrawalDetailPage })))
const WithdrawalSuccessPage = lazy(() => import('@/pages/withdrawals/WithdrawalSuccessPage').then(m => ({ default: m.WithdrawalSuccessPage })))
const NotificationsPage = lazy(() => import('@/pages/notifications/NotificationsPage').then(m => ({ default: m.NotificationsPage })))
const SettingsPage = lazy(() => import('@/pages/settings/SettingsPage').then(m => ({ default: m.SettingsPage })))
const SupportPage = lazy(() => import('@/pages/support/SupportPage').then(m => ({ default: m.SupportPage })))
const SupportChatPage = lazy(() => import('@/pages/support-chat/SupportChatPage').then(m => ({ default: m.SupportChatPage })))
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage').then(m => ({ default: m.NotFoundPage })))

function getApiBaseUrl(): string {
  // Runtime: injected per-tenant by the Pages middleware (zero-round-trip).
  // Production CF middleware writes `api_base_url` (already includes /api/v1);
  // older docs/legacy paths used `apiBase` (origin only). Handle both.
  const tc = window.__TENANT_CONFIG__ as Record<string, string | undefined> | undefined;
  if (tc?.api_base_url) return tc.api_base_url;
  if (tc?.apiBase) return `${tc.apiBase}/api/v1`;
  // Build-time override (CI / single-tenant deploy).
  const configured = import.meta.env.VITE_API_BASE_URL?.trim();
  if (configured) return configured;
  // Dev: rely on the Vite proxy (vite.config.ts) to forward /api/* to the
  // local backend. Avoids CORS, keeps cookies same-origin.
  if (import.meta.env.DEV) return '/api/v1';
  // Production without middleware: proxy through the Pages Function.
  return '/api/v1';
}

const API_BASE_URL = getApiBaseUrl()

// Kick off the integrations bootstrap immediately at module load. The fetch
// runs in parallel with React boot; firebaseConfig state updates when it
// resolves. CF Pages middleware-injected __TENANT_CONFIG__ may only carry
// tenant + branding + apiBase (R2 not always in sync with Postgres), so
// firebase, stripe, etc. arrive via this client-side fetch.
import { loadIntegrations, onIntegrationsReady } from './lib/integrations-bootstrap'
import { useAiEnabled } from './lib/use-ai-enabled'
import { refreshCurrencyConfigFromBackend } from '@cravo/utils'
void loadIntegrations(API_BASE_URL)
// Async currency refresh from the backend. `initCurrencyConfig()` in main.tsx
// already seeded from the inlined tenant config; this verifies against the
// authoritative `/platform-config/currency` and writes a tenant-scoped cache
// for the next visit. Subsequent `formatCurrency()` calls re-read the
// updated value, so any currency rendered before this resolves keeps the
// seeded value (no UI flicker for the common case where they match).
void refreshCurrencyConfigFromBackend(API_BASE_URL)

/**
 * Read current Firebase config from window.__TENANT_CONFIG__. Resolves to
 * undefined when the tenant hasn't configured Firebase — VendorAuthProvider
 * skips initializeApp() and the rest of the app still renders. Backend
 * emits snake_case; camelCase legacy fallback still accepted.
 */
function readFirebaseConfig() {
  const tf = (window.__TENANT_CONFIG__?.firebase ?? {}) as {
    enabled?: boolean
    api_key?: string; apiKey?: string
    auth_domain?: string; authDomain?: string
    project_id?: string; projectId?: string
    storage_bucket?: string; storageBucket?: string
    messaging_sender_id?: string; messagingSenderId?: string
    app_id?: string; appId?: string
  }
  const apiKey = tf.api_key ?? tf.apiKey
  const projectId = tf.project_id ?? tf.projectId
  // Honor explicit `enabled: false` even if creds happen to be present.
  if (tf.enabled === false || !apiKey || !projectId) return undefined
  return {
    apiKey,
    authDomain: tf.auth_domain ?? tf.authDomain ?? '',
    projectId,
    storageBucket: tf.storage_bucket ?? tf.storageBucket ?? '',
    messagingSenderId: tf.messaging_sender_id ?? tf.messagingSenderId ?? '',
    appId: tf.app_id ?? tf.appId ?? '',
  }
}

/**
 * Gate the /ai route so a bookmarked or pasted URL can't bypass the sidebar +
 * bubble hide on tenants without an AI integration. Strict: integrations
 * bootstrap must report ai.enabled === true.
 *
 * Renders nothing while the integrations bootstrap is in flight (the `ready`
 * flag), then commits: enabled → render the page, disabled → redirect to /.
 * We can't redirect during loading because tenants who DO have AI enabled
 * would briefly bounce to / on first paint before the bootstrap confirms.
 */
function AiRouteGuard({ children }: { children: React.ReactElement }) {
  const { enabled, ready } = useAiEnabled()
  // Don't redirect during the brief bootstrap window — that would punt a
  // tenant WITH AI enabled out of /ai on first paint before integrations
  // confirmed. Render nothing while we wait, then commit to a decision.
  if (!ready) return null
  if (!enabled) return <Navigate to="/" replace />
  return children
}

export default function App() {
  // Firebase config arrives via the bootstrap fetch (parallel to React boot).
  // Start with whatever __TENANT_CONFIG__ already has (could be undefined if
  // the middleware-injected config has no firebase block, which is common
  // when R2 lags Postgres). Re-read when the bootstrap resolves.
  // CRITICAL: always set the new value, even when readFirebaseConfig
  // returns undefined — that's how we detect a flipped `enabled: false`
  // on the live backend. Skipping the update on undefined leaves a
  // stale config and firebaseEnabled=true.
  const [firebaseConfig, setFirebaseConfig] = useState(() => readFirebaseConfig())
  useEffect(() => {
    onIntegrationsReady(() => {
      setFirebaseConfig(readFirebaseConfig())
    })
  }, [])
  const [gate, setGate] = useState<{
    mode: 'maintenance' | 'force_update'
    message?: string
    updateUrl?: string
  } | null>(null)

  // Boot-time platform config:
  //   1. /platform-config/version → maintenance / force-update gate
  //   2. /platform-config/currency → formatCurrency() override
  // Mobile parity: business_splash_controller.dart + currency_service.dart.
  useEffect(() => {
    platformConfigApi
      .getVersion()
      .then((cfg) => {
        if (cfg.maintenanceMode) {
          setGate({ mode: 'maintenance', message: cfg.maintenanceMessage })
          return
        }
        if (cfg.minWebVersion && compareVersions(APP_VERSION, cfg.minWebVersion) < 0) {
          setGate({ mode: 'force_update', updateUrl: cfg.updateUrl })
        }
      })
      .catch(() => {
        // Silent — backend may not expose this yet; treat as up-to-date.
      })

    // Currency fetch removed — `refreshCurrencyConfigFromBackend()` above
    // (kicked off at module load) handles backend currency with the correct
    // tenant-config-wins priority. The old `platformConfigApi.getCurrency()`
    // call always overwrote the tenant choice with the platform-wide default
    // (USD), which broke per-tenant currencies like AUD/PKR/INR.
  }, [])

  if (gate) {
    return <MaintenanceGate mode={gate.mode} message={gate.message} updateUrl={gate.updateUrl} />
  }

  return (
    <VendorAuthProvider apiBaseUrl={API_BASE_URL} firebaseConfig={firebaseConfig}>
      <PushNotificationsBootstrap />
      <Suspense fallback={null}>
        <Routes>
          {/* Guest-only auth routes */}
          <Route element={<GuestGuard><AuthLayout /></GuestGuard>}>
            <Route path="/welcome" element={<WelcomePage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/sign-up" element={<SignUpPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/phone-login" element={<PhoneLoginPage />} />
          </Route>

          {/* Auth required — onboarding (no business needed) */}
          <Route element={<AuthGuard><OnboardingLayout /></AuthGuard>}>
            {/* Owner Registration — mobile parity (OwnerRegistrationScreen).
                Shown right after signup/social-login until profile is complete
                (real phone + email). `/complete-profile` kept as legacy alias. */}
            <Route path="/onboarding/owner" element={<CompleteProfilePage />} />
            <Route path="/complete-profile" element={<Navigate to="/onboarding/owner" replace />} />
            <Route path="/onboarding" element={<EntityTypePage />} />
            <Route path="/onboarding/register" element={<BusinessRegistrationPage />} />
            <Route path="/onboarding/pending" element={<ApprovalPendingPage />} />
          </Route>

          {/* Auth + approved business required — dashboard */}
          <Route element={<AuthGuard><BusinessGuard><DashboardLayout /></BusinessGuard></AuthGuard>}>
            <Route path="/" element={<DashboardPage />} />
            <Route path="/requests" element={<RequestsPage />} />
            <Route path="/orders" element={<OrdersPage />} />
            <Route path="/orders/:orderId" element={<OrderDetailPage />} />
            <Route path="/orders/:orderId/chat" element={<OrderChatPage />} />
            <Route path="/ai" element={<AiRouteGuard><AiPage /></AiRouteGuard>} />
            <Route path="/menu" element={<MenuPage />} />
            <Route path="/analytics" element={<AnalyticsPage />} />
            <Route path="/analytics/items/:itemId" element={<ItemAnalyticsPage />} />
            <Route path="/feedback" element={<AnalyticsPage />} />
            <Route path="/wallet" element={<WalletPage />} />
            <Route path="/settlements" element={<SettlementsPage />} />
            <Route path="/bank" element={<BankAccountsPage />} />
            <Route path="/kyc" element={<KycPage />} />
            <Route path="/withdrawals" element={<WithdrawalHistoryPage />} />
            <Route path="/withdrawals/request" element={<WithdrawalRequestPage />} />
            <Route path="/withdrawals/success" element={<WithdrawalSuccessPage />} />
            <Route path="/withdrawals/:withdrawalId" element={<WithdrawalDetailPage />} />
            <Route path="/financials" element={<Navigate to="/wallet" replace />} />
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/support" element={<SupportPage />} />
            <Route path="/support/chat" element={<SupportChatPage />} />
          </Route>

          {/* Mobile-route compatibility aliases — push notifications, deep
              links and shared docs may use these names. */}
          <Route path="/home" element={<Navigate to="/" replace />} />
          <Route path="/dashboard" element={<Navigate to="/" replace />} />
          <Route path="/owner-registration" element={<Navigate to="/onboarding/owner" replace />} />
          <Route path="/entity-type-selection" element={<Navigate to="/onboarding" replace />} />
          <Route path="/restaurant-registration" element={<Navigate to="/onboarding/register" replace />} />
          <Route path="/approval-pending" element={<Navigate to="/onboarding/pending" replace />} />
          <Route path="/ai-chat" element={<Navigate to="/ai" replace />} />
          <Route path="/bank-management" element={<Navigate to="/bank" replace />} />
          <Route path="/customer-feedback" element={<Navigate to="/feedback" replace />} />
          <Route path="/menu-management" element={<Navigate to="/menu" replace />} />
          <Route path="/top-selling-items" element={<Navigate to="/analytics" replace />} />
          <Route path="/settlement-history" element={<Navigate to="/settlements" replace />} />
          <Route path="/wallet-screen" element={<Navigate to="/wallet" replace />} />

          {/* 404 */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </VendorAuthProvider>
  )
}
